// useYearlyTimesheets.js
import makeContext from '@libs/makeContext';

import {
    SET_EMAIL,
    SET_PAGE_NUMBER,
    SET_YEAR,
    SET_FILTERS,
    SET_PAGE_SIZE,
    TIMESHEET_NEXT_YEAR,
    TIMESHEET_PREV_YEAR
} from '../../context.actionTypes';

import { ADMIN_TIMESHEET_INTITAL_API_PARAMS } from '../common';

const initialState = {
    currentPage: 1,
    data: [],
    totalPages: 0,
    email: '',
    year: new Date().getFullYear(),
    isNextDisabled: true,
    pageSize: 10,
    filters: ADMIN_TIMESHEET_INTITAL_API_PARAMS.filters
};

const reducer = (state, { type, payload }) => {
    switch (type) {
        case SET_EMAIL:
            return { ...state, email: payload };

        case SET_YEAR: {
            const currentYear = new Date().getFullYear();
            const newYear = Number(payload);
            return {
                ...state,
                year: newYear,
                // disable "next" when selected year is the current year
                isNextDisabled: newYear >= currentYear
            };
        }

        case SET_FILTERS:
            return { ...state, filters: payload };

        case SET_PAGE_SIZE:
            return { ...state, pageSize: payload };

        default:
            return state;
    }
};

const middleware = (state, action, { fetchData }) => {
    switch (action.type) {
        case SET_PAGE_NUMBER: {
            const { email, year, filters, pageSize } = state;
            const { payload } = action;
            fetchData({
                year,
                email,
                filters,
                pageSize,
                pageNumber: payload
            });
            return action;
        }

        case SET_PAGE_SIZE: {
            const { email, year, filters } = state;
            const { payload } = action;
            fetchData({
                year,
                email,
                filters,
                pageSize: payload,
                pageNumber: 1
            });
            return action;
        }

        case SET_EMAIL: {
            const { year, filters, pageSize } = state;
            const { payload } = action;
            fetchData({
                year,
                email: payload,
                filters,
                pageSize,
                pageNumber: 1
            });
            return action;
        }

        case SET_YEAR: {
            const { email, filters, pageSize } = state;
            const { payload } = action; // payload should be year (number or string)
            fetchData({
                year: Number(payload),
                email,
                filters,
                pageSize,
                pageNumber: 1
            });

            return action;
        }

        case TIMESHEET_NEXT_YEAR: {
            const { year, email, filters, pageSize } = state;
            const nextYear = Number(year) + 1;

            // guard: don't request future beyond current year if you want that behavior
            fetchData({
                year: nextYear,
                email,
                filters,
                pageSize,
                pageNumber: 1
            });

            return {
                type: SET_YEAR,
                payload: nextYear
            };
        }

        case TIMESHEET_PREV_YEAR: {
            const { year, email, filters, pageSize } = state;
            const prevYear = Number(year) - 1;

            fetchData({
                year: prevYear,
                email,
                filters,
                pageSize,
                pageNumber: 1
            });

            return { type: SET_YEAR, payload: prevYear };
        }

        case SET_FILTERS: {
            const { year, email, filters, pageSize } = state;
            const { filterType, value } = action.payload;

            const newFilters = { ...filters, [filterType]: value || null };

            fetchData({
                year,
                email,
                filters: newFilters,
                pageSize,
                pageNumber: 1
            });

            return { ...action, payload: { ...newFilters } };
        }

        default:
            return action;
    }
};

const [
    YearlyTimesheetsProvider,
    useYearlyTimesheetsState,
    useYearlyTimesheetsDispatch,
    useYearlyTimesheets
] = makeContext(reducer, initialState, {
    name: 'YearlyTimesheets',
    middleware
});

export {
    useYearlyTimesheets as default,
    YearlyTimesheetsProvider,
    useYearlyTimesheetsState,
    useYearlyTimesheetsDispatch,
    SET_EMAIL,
    SET_PAGE_NUMBER,
    SET_YEAR,
    SET_FILTERS,
    SET_PAGE_SIZE,
    TIMESHEET_NEXT_YEAR,
    TIMESHEET_PREV_YEAR
};
